import ApiCollection                      from '@widesk-core/models/ApiCollection';
import { Box }                            from '@chakra-ui/react';
import { HiCheck }                        from "react-icons/hi"
import NewsletterModel                    from '@/models/services/notification/NewsletterModel'
import React                              from 'react';
import SubscribeModel                     from '@/models/services/notification/SubscribeModel';
import SubscriptionModel                  from '@/models/services/notification/SubscriptionModel';
import SubscriptionStatusHistoryModel     from '@/models/services/notification/SubscriptionStatusHistoryModel';
import SubscriptionStatusModel            from '@/models/services/notification/SubscriptionStatusModel';
import { Switch }                         from "@/components/ui/switch"
import { Tag }                            from '@chakra-ui/react';
import { Text }                           from '@chakra-ui/react';
import { toaster }                        from '@/components/ui/toaster';
import { useTranslation }                 from 'react-i18next';
import { ModalData }                      from '@/components/modal/Modal';
import Spinner                            from '@/components/spinner/Spinner';

interface INewslettersListRowProps {
	newsletter: NewsletterModel;
	subscriptionCollection: ApiCollection<SubscriptionModel>;
	subscriptionStatusCollection: ApiCollection<SubscriptionStatusModel>;
	setModalData: React.Dispatch<React.SetStateAction<ModalData | undefined>>;
	setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const NewslettersListRow = (props: INewslettersListRowProps) => {

	const { newsletter, subscriptionCollection, subscriptionStatusCollection, setModalData, setModalIsOpen } = props;
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = React.useState(false);
	
	const existingSubscription = subscriptionCollection.find(subscription => subscription.channel.urn === newsletter.urn);
	const disabledStatus = subscriptionStatusCollection.models.find(status => status.reference === 'disabled') as SubscriptionStatusModel;
	const enabledStatus = subscriptionStatusCollection.models.find(status => status.reference === 'enabled') as SubscriptionStatusModel;

	const isChecked = !!existingSubscription && existingSubscription.getUrn('subscriptionStatus') === enabledStatus.urn;

	const handleUnsubscription = async () => {

		setModalData({
			isDangerous: true,
			setIsOpen: setModalIsOpen,
			titleComponent: <Text>{t('pages.newsletters.modals.unsubscribe.title')}</Text>,
			bodyComponent: <Text>{t('pages.newsletters.modals.unsubscribe.message', { label: newsletter?.label })}</Text>,
			onClose: () => {
				setModalIsOpen(false);
				setModalData(undefined);
			},
			onConfirm: async () => {
				setIsLoading(true);
				if (existingSubscription) {
					await new SubscriptionStatusHistoryModel().patch({
						subscription: existingSubscription.urn,
						subscriptionStatus: disabledStatus.urn,
					});
				}
			},
			onError: () => {
				toaster.create({
					title: t('app.errors.unknown'),
					type: 'error',
					duration: 5000,
				});
				setIsLoading(false);
			},
			onSuccess: async () => {
				toaster.create({
					title: t('pages.newsletters.actions.unsubscribe.success'),
					type: 'success',
					duration: 5000,
				});
				await subscriptionCollection.list();
				await subscriptionCollection.whenIsLoaded(m => m.subscriptionStatus);
				setIsLoading(false);
				setModalIsOpen(false);
				setModalData(undefined);
			}
		})
		setModalIsOpen(true);
	}

	const handleSubscription = async (event: any) => {
		const { checked } = event.target;
		if (checked) {
			
			if (existingSubscription) {
				try {
					setIsLoading(true);
					await new SubscriptionStatusHistoryModel().patch({
						subscription: existingSubscription.urn,
						subscriptionStatus: enabledStatus.urn,
					});
					await subscriptionCollection.list();
					await subscriptionCollection.whenIsLoaded(m => m.subscriptionStatus);
					toaster.create({
						title: t('pages.newsletters.actions.subscribe.success', { label: newsletter.label }),
						type: 'success',
						duration: 5000,
					});
				} catch (error) {
					toaster.create({
						title: t('app.errors.unknown'),
						type: 'error',
						duration: 5000,
					});
					console.log(error);
				} finally {
					setIsLoading(false);
				}
			} else {
				try {
					setIsLoading(true);
					await new SubscribeModel().patch({ channel: newsletter.urn });
					await subscriptionCollection.list();
					await subscriptionCollection.whenIsLoaded(m => m.subscriptionStatus);
					toaster.create({
						title: t('pages.newsletters.actions.subscribe.success', { label: newsletter.label }),
						type: 'success',
						duration: 5000,
					});
				} catch (error) {
					toaster.create({
						title: t('app.errors.unknown'),
						type: 'error',
						duration: 5000,
					});
					console.log(error);
				} finally {
					setIsLoading(false);
				}
			}
		}
		if (checked === false) {
			handleUnsubscription();
		}
		
	}

	return (
		<Box
			display="flex"
			alignItems="center"
			justifyContent="space-between"
		>
			{
				isLoading
				? (
					<Spinner />
				)
				: (
					<Box
						display="flex"
						flexDirection="column"
						gap="10px"
					>
						<Text
							className="poppins-semibold"
						>
							{newsletter.label}
						</Text>
						{
							isChecked &&
							<Box>
								<Tag.Root colorPalette="green">
									<Tag.StartElement>
										<HiCheck />
									</Tag.StartElement>
									<Tag.Label>{t('pages.newsletters.subscriber')}</Tag.Label>
								</Tag.Root>
							</Box>
						}
					</Box>
				)
			}
			<Switch colorPalette="teal" size="lg" onClick={handleSubscription} checked={isChecked}/>
		</Box>
	)
}

export default NewslettersListRow